import React, { useEffect, useState } from "react";
import { Anchor, Modal, MultiSelect, PasswordInput, Stack, Text, TextInput } from "@mantine/core";
import { Link } from "react-router-dom";
import { Alert, Button } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type {
  CredentialWrite,
  ModuleOption,
  ProviderAdapter,
  ProviderCredential,
  ProviderFieldSpec,
} from "@models/providerCatalog";
import { errorMessage } from "@hooks/monitoring/useApiResource";

interface CredentialModalProps {
  opened: boolean;
  provider: ProviderAdapter | null;
  /** The credential being edited; `null` creates a new one. */
  credential: ProviderCredential | null;
  /** The modules a credential can be granted to; `null` while they load or could not load. */
  modules: ModuleOption[] | null;
  onClose: () => void;
  onSave: (providerKey: string, credentialId: string | null, body: CredentialWrite) => Promise<void>;
}

const shown = (spec: ProviderFieldSpec) => spec.use !== "unused";

/**
 * Create or replace one provider credential and pick the modules it is granted to.
 * Core writes one SSM copy per module, read only by that module's service. Only the fields
 * the provider's runtime adapter reads are shown. Core never reads a key back, so a provider
 * that uses one needs it on every save (rotation is the same form). Core validates the
 * result and its message is shown as is.
 */
const CredentialModal: React.FC<CredentialModalProps> = ({
  opened, provider, credential, modules, onClose, onSave,
}) => {
  const { t } = useTranslation();
  const editing = credential !== null;
  const [granted, setGranted] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [apiVersion, setApiVersion] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setGranted(credential?.modules.map((m) => m.id) ?? []);
    setName(credential?.name ?? "");
    setApiKey("");
    setEndpoint(credential?.endpoint ?? "");
    setApiVersion(credential?.api_version ?? "");
    setError(null);
  }, [opened, credential]);

  if (!provider) return null;

  // A module the credential holds that is no longer registered stays selectable, by its key.
  const options = [
    ...(modules ?? []).map((m) => ({ value: m.id, label: m.name })),
    ...(credential?.modules ?? [])
      .filter((held) => !(modules ?? []).some((m) => m.id === held.id))
      .map((held) => ({ value: held.id, label: held.name ?? held.id })),
  ];
  const usesKey = shown(provider.api_key) && provider.auth_mode !== "iam";
  const keyRequired = usesKey && (provider.api_key.use === "required" || Boolean(credential?.has_api_key));
  const missing =
    granted.length === 0 ||
    (keyRequired && !apiKey.trim()) ||
    (provider.endpoint.use === "required" && !endpoint.trim()) ||
    (provider.api_version.use === "required" && !apiVersion.trim());

  const submit = async () => {
    const body: CredentialWrite = { modules: granted };
    if (name.trim()) body.name = name.trim();
    if (usesKey && apiKey.trim()) body.api_key = apiKey.trim();
    if (shown(provider.endpoint)) body.endpoint = endpoint.trim();
    if (shown(provider.api_version)) body.api_version = apiVersion.trim();
    setSaving(true);
    setError(null);
    try {
      await onSave(provider.key, credential?.id ?? null, body);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t(editing ? "admin.providers.form.editTitle" : "admin.providers.form.addTitle", { provider: provider.label })}
      size="lg"
      portalProps={{ target: "#admin-portal-root" }}
    >
      <Stack gap="md">
        {provider.auth_mode === "iam" && (
          <Alert color="blue">{t("admin.providers.form.iamNote")}</Alert>
        )}
        <MultiSelect
          label={t("admin.providers.form.modules")}
          description={t("admin.providers.form.modulesHint")}
          data={options}
          value={granted}
          onChange={setGranted}
          searchable
          nothingFoundMessage={t("admin.providers.form.noModules")}
          placeholder={modules === null ? t("admin.providers.form.modulesLoading") : undefined}
          error={modules !== null && modules.length === 0 ? t("admin.providers.form.noModules") : undefined}
          required
          comboboxProps={{ withinPortal: false }}
        />
        {modules !== null && modules.length === 0 && (
          <Anchor component={Link} to="/admin/access/modules" size="sm">{t("admin.providers.form.createModule")}</Anchor>
        )}
        <TextInput
          label={t("admin.providers.form.name")}
          placeholder={provider.label}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        {usesKey && (
          <PasswordInput
            label={t("admin.providers.form.apiKey")}
            description={editing ? t("admin.providers.form.apiKeyAgain") : undefined}
            value={apiKey}
            onChange={(e) => setApiKey(e.currentTarget.value)}
            required={keyRequired}
            autoComplete="new-password"
          />
        )}
        {shown(provider.endpoint) && (
          <TextInput
            label={t("admin.providers.form.endpoint")}
            description={provider.endpoint.hint ?? undefined}
            value={endpoint}
            onChange={(e) => setEndpoint(e.currentTarget.value)}
            required={provider.endpoint.use === "required"}
          />
        )}
        {shown(provider.api_version) && (
          <TextInput
            label={t("admin.providers.form.apiVersion")}
            description={provider.api_version.hint ?? undefined}
            value={apiVersion}
            onChange={(e) => setApiVersion(e.currentTarget.value)}
            required={provider.api_version.use === "required"}
          />
        )}
        <Text size="xs" c="dimmed">{t("admin.providers.form.propagation")}</Text>
        {error && <Alert color="red">{error}</Alert>}
        <Stack gap="xs" align="flex-end">
          <Button onClick={submit} loading={saving} disabled={missing}>
            {t(editing ? "admin.providers.form.save" : "admin.providers.form.add")}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default CredentialModal;
